import { slugify } from "@utilities/slugify";
import { Typography, type TypographyProps } from "./Typography";

type RichTextProps = {
  content?: null | string;
  headingIdPrefix?: string;
} & Omit<TypographyProps, "children" | "dangerouslySetInnerHTML">;

export function fixRichTextLinks(content: null | string | undefined) {
  // Matches links that start with https or http or are just the plain domain name without protocol.
  // Avoids matching links to subdomains.
  return content?.replace(/href="((?:https?:\/\/|\/\/)?(?:elevenlabs\.io|eleven2\.dev)\/[^"]*)"/g, (match, url) => {
    const elevenlabsIndex = url.indexOf("elevenlabs.io/");
    const eleven2Index = url.indexOf("eleven2.dev/");

    if (elevenlabsIndex !== -1) {
      const pathAfterDomain = url.substring(elevenlabsIndex + "elevenlabs.io/".length);
      return `href="/${pathAfterDomain}"`;
    } else if (eleven2Index !== -1) {
      const pathAfterDomain = url.substring(eleven2Index + "eleven2.dev/".length);
      return `href="/${pathAfterDomain}"`;
    }
    return match;
  });
}

export function isRichTextEmpty(content: null | string | undefined) {
  if (!content) return true;
  const trimmedContent = content.trim();
  return !trimmedContent || trimmedContent === "<p></p>";
}

const RichText = ({ content, headingIdPrefix, ...typographyProps }: RichTextProps) => {
  const foundHeadings: { [key: string]: number } = {};
  let processedContent = content?.replace(/<(h[234])([^>]*)>(.*?)<\/h[234]>/g, (match, tag, attrs, text) => {
    // Remove any HTML tags to get just the text content
    const plainText = text.replace(/<[^>]*>/g, "");
    const id = `${headingIdPrefix ? `${headingIdPrefix}-` : ""}${slugify(plainText)}`;
    const idToUse = foundHeadings[id] ? `${id}-${foundHeadings[id]}` : id;
    foundHeadings[id] = (foundHeadings[id] || 0) + 1;
    return `<${tag}${attrs} id="${idToUse}">${text}</${tag}>`;
  });

  // Handles people typing the wrong urls or not https.
  processedContent = fixRichTextLinks(processedContent);

  return processedContent && <Typography {...typographyProps} dangerouslySetInnerHTML={{ __html: processedContent }} />;
};

RichText.displayName = "RichText";

export { RichText };
